/**
 * What Arrange chooses for the reader: where arranging starts, and what the arranger may add.
 *
 * The reader gives a score and says who plays. Every other setting the lab has is chosen here,
 * for the roster and the score, and each for a reason that was counted (docs/07, "What Arrange
 * chooses"):
 *
 * - It starts from the score, which hands the players the score's own notes and writes none of
 *   its own, whoever plays. A guitar too: from the score it is handed the tune, the bass and
 *   the chords between that its hand stops, three quarters of the score's notes alone and nine
 *   in ten beside others, where the note model keeps the tune, a third, and writes the rest.
 * - From the score the check is made. Where the roster is larger than the score's staves,
 *   players with nothing to play double a line and the harmony is filled in where a chord is
 *   short of a note: they are what gives its players something to play. Where it is not, the
 *   score is played as written, and the harmony filled in only where the score prints chord
 *   symbols, which are all a lead sheet's accompaniment is. Doubling is octaves for a pianist
 *   or a harpist alone: over the 31 sample pieces a pianist alone left 1,361 faults with both
 *   on, 9,966 notes doubled, and leaves 862 with the score as written (Für Elise had 1,713
 *   notes for its 901 and 18 faults for 5); a harpist 1,442 for 888, a violin and a piano 811
 *   for 769, and every other roster counted the same.
 * - The lines are shared by turns, so that no player of an orchestra waits through the piece:
 *   over the 22 sample pieces the Classical orchestra has no player with no note where 17 had
 *   none, for 30 faults more (189 for 159), and the string quartet's are as they were.
 * - The whole piece is arranged, take 1.
 *
 * A wish in words is read against these settings and may change any of them (`lib/ask-run`);
 * where it names other players, what it left as it was is chosen again for them (`chosenAgain`).
 * Nothing is remembered from one press of Arrange to the next: each begins from what is chosen
 * here for the players then named, so the same score, players and words arrange the same.
 */

import type { Score } from "@app/score-editor";
import { type Choices, NO_CHOICES } from "../components/ArrangeChoices";
import { NO_REVIEW, requested } from "../components/ArrangeReview";
import { askedWindow, type Board } from "./ask-run";
import { sameSeats } from "./roster";

/** What the rules arranger may add, starting from the score. */
const CHOSEN: Choices = { ...NO_CHOICES, harmony: "fill", double: true, share: true };

/** What is chosen where the score has a staff for every player: its notes as written. */
const WRITTEN: Choices = { ...NO_CHOICES, share: true };

/** What of a score the choices are made for: how many staves it has, and chord symbols. */
export interface ScoreShape {
  readonly staves: number;
  readonly chords: boolean;
}

/** The shape of `score`, or `null` where none is read yet. */
export function shapeOf(score: Score | null): ScoreShape | null {
  if (score === null) return null;
  return {
    staves: score.parts.reduce((sum, part) => sum + part.staves.length, 0),
    chords: score.parts.some((part) =>
      Object.values(part.measures).some((bar) => bar.harmonies.length > 0),
    ),
  };
}

/** What may be added for `roster` in a score of `shape`. */
function choicesFor(roster: readonly string[], shape: ScoreShape | null): Choices {
  if (shape === null || roster.length > shape.staves) return CHOSEN;
  return shape.chords ? { ...WRITTEN, harmony: "fill" } : WRITTEN;
}

/** Every setting chosen for `roster` and a score of `shape`: the whole piece, as Arrange sends it. */
export function boardFor(roster: readonly string[], shape: ScoreShape | null): Board {
  return {
    roster,
    choices: choicesFor(roster, shape),
    review: { ...NO_REVIEW, check: true },
    whole: true,
    first: "1",
    // No last bar typed is the score's own last bar, wherever a wish asks for a window.
    last: "",
  };
}

/**
 * `next`, as the answer to words read against `sent` set it, with what may be added chosen
 * again for the players it names where they are others than `sent`'s and the answer left a
 * choice as it was: a flute added to a quartet in words is given what five players over four
 * staves are given when named in the box.
 */
export function chosenAgain(sent: Board, next: Board, shape: ScoreShape | null): Board {
  if (sameSeats(sent.roster, next.roster)) return next;
  const chosen = choicesFor(next.roster, shape);
  const keys = Object.keys(chosen) as (keyof Choices)[];
  const choices = keys.reduce<Choices>(
    (made, key) =>
      next.choices[key] === sent.choices[key] ? { ...made, [key]: chosen[key] } : made,
    next.choices,
  );
  return { ...next, choices };
}

/** The bars `board` asks for, where they are fewer than the `total` the score has. */
function fewer(board: Board, total: number | null): string | null {
  const bars = askedWindow(board);
  if (bars === undefined || (bars[0] <= 1 && total !== null && bars[1] >= total)) return null;
  return bars.join("-");
}

/**
 * Whether `next` is arranged exactly as `sent` is, in a score of `total` bars: the same
 * players and bars, and the same of everything chosen. Said of an answer that left the wish
 * undone without saying so; where the two only might differ they are taken to, so it is never
 * said of words that did something.
 */
export function unchanged(sent: Board, next: Board, total: number | null): boolean {
  const keys = Object.keys(sent.choices) as (keyof Choices)[];
  return (
    sameSeats(sent.roster, next.roster) &&
    fewer(sent, total) === fewer(next, total) &&
    JSON.stringify(requested(sent.review)) === JSON.stringify(requested(next.review)) &&
    (next.review.start !== "score" || keys.every((key) => sent.choices[key] === next.choices[key]))
  );
}
